import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { filterAvailableFilters } from 'utils/filterPermissions';
import { normalizeContextFileSelections } from 'utils/contextFileSelections';
import type { ContextFileSelection } from 'types/contextDocuments';

const FILTERS_STORAGE_KEY = 'chat_source_filters';

export interface SourceFiltersState {
  use_source_common: boolean;
  use_source_ai_config: boolean;
  use_specification: string | null;
  use_source_jira: boolean;
  use_source_confluence: boolean;
  use_source_gitLab: boolean;
  use_source_yandex_tracker: boolean;
  use_source_yandex_wiki: boolean;
  use_source_url_ids: string[];
  use_context_chat: boolean;
  context_mode: 'all' | 'limit';
  context_messages_limit: number;
  show_specification_select: boolean;
  use_context_files: ContextFileSelection[];
}

const DEFAULT_FILTERS: SourceFiltersState = {
  use_source_common: false,
  use_source_ai_config: false,
  use_specification: null,
  use_source_jira: false,
  use_source_confluence: false,
  use_source_gitLab: false,
  use_source_yandex_tracker: false,
  use_source_yandex_wiki: false,
  use_source_url_ids: [],
  use_context_chat: true,
  context_mode: 'all',
  context_messages_limit: 10,
  show_specification_select: false,
  use_context_files: [],
};

export const useChatFilters = () => {
  const [sourceFilters, setSourceFilters] = useState<SourceFiltersState>(DEFAULT_FILTERS);
  const [isInitialized, setIsInitialized] = useState(false);
  const userRole = useSelector((state: { reducer: { role?: unknown } }) => state.reducer.role);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Record<string, unknown> | null;
        if (parsed && typeof parsed === 'object') {
          const mergedFilters = { ...DEFAULT_FILTERS };
          (Object.keys(DEFAULT_FILTERS) as (keyof SourceFiltersState)[]).forEach((key) => {
            if (key in parsed) {
              if (key === 'context_messages_limit') {
                mergedFilters[key] =
                  typeof parsed[key] === 'number' ? (parsed[key] as number) : DEFAULT_FILTERS[key];
              } else if (key === 'context_mode') {
                mergedFilters[key] =
                  typeof parsed[key] === 'string'
                    ? (parsed[key] as 'all' | 'limit')
                    : DEFAULT_FILTERS[key];
              } else if (key === 'use_specification') {
                const parsedValue = parsed[key];
                if (
                  parsedValue === null ||
                  parsedValue === undefined ||
                  parsedValue === false ||
                  parsedValue === true
                ) {
                  mergedFilters[key] = null;
                } else {
                  mergedFilters[key] = String(parsedValue);
                }
              } else if (key === 'use_context_files') {
                // Миграция: до обновления хранили string[], теперь ContextFileSelection[]
                mergedFilters[key] = normalizeContextFileSelections(parsed[key]);
              } else if (key === 'use_source_url_ids') {
                mergedFilters[key] = Array.isArray(parsed[key])
                  ? (parsed[key] as string[]).filter((v) => typeof v === 'string')
                  : DEFAULT_FILTERS[key];
              } else if (key === 'show_specification_select') {
                mergedFilters[key] = Boolean(parsed[key]);
              } else {
                mergedFilters[key] = Boolean(parsed[key]) as never;
              }
            }
          });
          if (!mergedFilters.show_specification_select) {
            mergedFilters.use_specification = null;
          }
          const cleanedFilters = userRole
            ? (filterAvailableFilters(mergedFilters, userRole) as SourceFiltersState)
            : mergedFilters;
          setSourceFilters(cleanedFilters);
        } else {
          setSourceFilters(DEFAULT_FILTERS);
        }
      } else {
        setSourceFilters(DEFAULT_FILTERS);
      }
    } catch (error) {
      console.warn('Failed to load chat filters from localStorage:', error);
      setSourceFilters(DEFAULT_FILTERS);
    } finally {
      setIsInitialized(true);
    }
  }, [userRole]);

  useEffect(() => {
    if (isInitialized && userRole) {
      setSourceFilters((prevFilters) => {
        const cleanedFilters = filterAvailableFilters(prevFilters, userRole) as SourceFiltersState;
        const hasChanges = (Object.keys(cleanedFilters) as (keyof SourceFiltersState)[]).some(
          (key) => cleanedFilters[key] !== prevFilters[key]
        );
        return hasChanges ? cleanedFilters : prevFilters;
      });
    }
  }, [userRole, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      try {
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(sourceFilters));
      } catch (error) {
        console.warn('Failed to save chat filters to localStorage:', error);
      }
    }
  }, [sourceFilters, isInitialized]);

  const toggleFilter = (key: keyof SourceFiltersState) => {
    setSourceFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleContextChat = () => {
    setSourceFilters((prev) => ({ ...prev, use_context_chat: !prev.use_context_chat }));
  };

  const updateContextMode = (mode: 'all' | 'limit') => {
    setSourceFilters((prev) => ({ ...prev, context_mode: mode }));
  };

  const updateContextLimit = (value: string) => {
    const numValue = parseInt(value, 10);
    const processedValue = isNaN(numValue) ? 10 : numValue;
    setSourceFilters((prev) => ({ ...prev, context_messages_limit: processedValue }));
  };

  const updateSpecification = (id: string | null) => {
    setSourceFilters((prev) => ({ ...prev, use_specification: id }));
  };

  const toggleSpecificationSelect = () => {
    setSourceFilters((prev) => ({
      ...prev,
      show_specification_select: !prev.show_specification_select,
      use_specification: !prev.show_specification_select ? prev.use_specification : null,
    }));
  };

  const updateContextFiles = (selections: ContextFileSelection[]) => {
    setSourceFilters((prev) => ({ ...prev, use_context_files: selections }));
  };

  const updateUrlSourceIds = (ids: string[]) => {
    setSourceFilters((prev) => ({ ...prev, use_source_url_ids: ids }));
  };

  const getContextMessagesLimit = (): number | null => {
    if (!sourceFilters.use_context_chat) return 0;
    if (sourceFilters.context_mode === 'all') return null;
    return sourceFilters.context_messages_limit;
  };

  return {
    sourceFilters,
    toggleFilter,
    toggleContextChat,
    updateContextMode,
    updateContextLimit,
    updateSpecification,
    toggleSpecificationSelect,
    updateContextFiles,
    updateUrlSourceIds,
    getContextMessagesLimit,
    isInitialized,
  };
};
